import { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { Toaster } from 'sonner';
import { 
  HelpCircle, Book, MessageCircle, Shield, Zap, Users, 
  ChevronDown, ChevronUp, ExternalLink, Search, FileText,
  Clock, CheckCircle, AlertCircle
} from 'lucide-react';

const SupportPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: 'How does AcuSightPro handle data security?',
      answer: 'AcuSightPro is built with enterprise-grade security at its core. We use AES-256 encryption for data at rest, TLS 1.3 for data in transit, and maintain SOC 2 Type II compliance. All data is stored in FedRAMP-authorized cloud infrastructure, and we support single sign-on (SSO) integration with your existing identity providers.'
    },
    {
      question: 'What integrations does AcuSightPro support?',
      answer: 'AcuSightPro integrates with popular document management systems, financial systems, and HR platforms. We offer secure connectors for SharePoint, SAP, Oracle, and many others. Our API allows for custom integrations with your existing systems of record.'
    },
    {
      question: 'How long does implementation typically take?',
      answer: 'Most organizations are up and running within 2-4 weeks. This includes initial configuration, user training, and integration with your existing systems. Our implementation team works closely with your IT and acquisition staff to ensure a smooth rollout.'
    },
    {
      question: 'Can I customize workflows for my agency?',
      answer: 'Absolutely! AcuSightPro is designed to be flexible. You can configure workflows, checklists, templates, and approval chains to match your agency\'s specific requirements and threshold policies. Our team can help you set up configurations that align with your processes.'
    },
    {
      question: 'What training and onboarding is provided?',
      answer: 'We provide comprehensive onboarding that includes live training sessions, video tutorials, and detailed documentation. Our customer success team offers ongoing support and can provide role-specific training for your acquisition leadership, contracting officers, and diligence teams.'
    },
    {
      question: 'How does the AI ensure explainability and auditability?',
      answer: 'Every AI-generated recommendation, flag, or draft in AcuSightPro is linked to the source data that triggered it. Users can always see why the AI made a particular suggestion, and all AI outputs go through human review before becoming final. Complete audit trails are maintained for compliance and oversight.'
    },
    {
      question: 'Is there a free trial available?',
      answer: 'We offer personalized demos and pilot programs for qualified organizations. Contact our sales team to discuss your needs and explore options for a proof-of-concept deployment tailored to your specific use cases.'
    },
    {
      question: 'What support channels are available?',
      answer: 'We offer multiple support channels including email, phone, and live chat during business hours. Enterprise customers have access to dedicated support contacts and priority response times. Our knowledge base is available 24/7 for self-service support.'
    },
    {
      question: 'How does AcuSightPro help prevent bid protests?',
      answer: 'AcuSightPro automatically generates protest-ready documentation throughout the acquisition lifecycle. Every evaluation score links to source evidence, every decision has an audit trail, and AI-flagged compliance issues are resolved before they become vulnerabilities. This comprehensive documentation significantly reduces protest risk and strengthens your position if a protest does occur.'
    },
    {
      question: 'Can AcuSightPro handle classified or CUI data?',
      answer: 'Yes. AcuSightPro supports Controlled Unclassified Information (CUI) handling with appropriate safeguards. We operate on FedRAMP-authorized infrastructure and can configure environments to meet NIST 800-171 requirements. For classified work, contact our team to discuss deployment options.'
    },
    {
      question: 'What happens if the AI makes an error?',
      answer: 'ACUMEN-10 is designed with human-in-the-loop oversight. All AI outputs require human review and approval before taking effect. The system also features self-healing capabilities that learn from corrections and improve over time. Our 4-tier failover architecture ensures 99.97% uptime even if individual components experience issues.'
    },
    {
      question: 'How does pricing work?',
      answer: 'AcuSightPro pricing is based on the number of users and the modules you need. We offer flexible subscription models for agencies of all sizes. Contact our sales team for a customized quote based on your organization\'s requirements and expected volume.'
    },
    {
      question: 'Can multiple teams collaborate on the same acquisition?',
      answer: 'Yes! Real-time collaboration is a core feature. Legal, Budget, HR, Tech, Security, and Integration teams can all work simultaneously in shared workspaces with role-based permissions. Changes sync instantly, eliminating version control issues and email coordination overhead.'
    },
    {
      question: 'How current is the FAR/DFARS compliance checking?',
      answer: 'Our compliance database is updated within 48 hours of any FAR/DFARS changes. ACUMEN-10 continuously monitors regulatory updates and automatically flags any documents or workflows that may be affected by new requirements. You\'ll never be caught off-guard by regulatory changes.'
    },
    {
      question: 'What reporting and analytics are available?',
      answer: 'AcuSightPro provides comprehensive dashboards showing pipeline status, cycle times, team workload, funding risk, and vendor performance across all active acquisitions. Reports can be exported for leadership briefings, and custom reports can be configured to match your agency\'s KPIs.'
    },
    {
      question: 'Is AcuSightPro accessible for users with disabilities?',
      answer: 'Yes. AcuSightPro is designed to meet WCAG 2.1 AA accessibility standards. We support screen readers, keyboard navigation, and other assistive technologies to ensure all users can effectively use the platform.'
    }
  ];

  const filteredFaqs = faqs.filter(
    faq =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const supportStats = [
    { icon: Clock, value: '< 2 hrs', label: 'Average Response Time' },
    { icon: CheckCircle, value: '98%', label: 'Customer Satisfaction' },
    { icon: AlertCircle, value: '24/7', label: 'Critical Issue Support' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <CustomCursor />
      <Toaster position="top-center" />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-20 pb-12 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 via-transparent to-purple-100/50"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-6">
                Support Center
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                How Can We
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                  Help You Today?
                </span>
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Find answers, get support, and make the most of AcuSightPro.
              </p>

              {/* Search Bar */}
              <div className="relative max-w-2xl mx-auto">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all outline-none shadow-lg shadow-gray-100"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Support Stats */}
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-3 gap-6">
              {supportStats.map((stat, index) => (
                <div key={index} className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <stat.icon className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ACUMEN-10 AI Assistant Section */}
        <section className="py-12 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-3xl font-bold text-white border border-white/30">
                  A7
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold text-white mb-2">
                  Meet ACUMEN-10 Assistant
                </h3>
                <p className="text-indigo-100 mb-4">
                  Get instant answers powered by our AI trained on federal acquisition. 
                  ACUMEN-10 can help with FAR/DFARS questions, document guidance, workflow navigation, 
                  and more, 24/7, no wait time.
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm text-white">FAR/DFARS Guidance</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm text-white">Document Help</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm text-white">Workflow Tips</span>
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm text-white">24/7 Available</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <HelpCircle className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600">
                Quick answers to common questions about AcuSightPro
              </p>
            </div>

            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No results found for &quot;{searchQuery}&quot;
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredFaqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden h-fit"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 pr-8">
                        {faq.question}
                      </span>
                      {openFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-6">
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Still Need Help?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Our support team is ready to assist you with any questions or issues.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/30 transition-all hover:-translate-y-0.5"
              >
                Contact Support
              </a>
              <a
                href="mailto:support@acusightpro.com"
                className="px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:border-purple-300 hover:text-purple-700 transition-all flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Email Support
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const Mail = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

export default SupportPage;
